import { useEffect, useState } from "react";
import type { JSX } from "react";
import { isRouteErrorResponse, Link, useRouteError } from "react-router";
import { useOnlineStatus } from "../offline/useOnlineStatus";
import { useI18n } from "../shared/i18n/I18nContext";
import { useClientLog } from "../shared/logging/ClientLogContext";
import { useAnnouncer } from "../shared/ui/Announcer";
import { cx } from "../shared/ui/classNames";
import { PageHeader } from "../shared/ui/PageHeader";
import { PublicLayout } from "../shared/ui/PublicLayout";
import styles from "../shared/ui/ui.module.css";
import { isChunkLoadError } from "./chunkLoadError";
import { routePaths } from "./navigation";

/** Kind of error page. */
type ErrorKind = "notFound" | "unavailable" | "generic";

/** Title and content of an error page, placed inside or outside the app shell. */
interface ErrorView {
  /** Page title: the level-one heading and the document title. */
  readonly title: string;
  /** Content below the heading. */
  readonly content: JSX.Element;
}

/**
 * Loads the application again, which also fetches the code of the screen again.
 */
function reloadPage(): void {
  window.location.reload();
}

/**
 * Classifies an error reported by a route: an unknown path, a screen whose code could not be
 * loaded (UX-109) or anything else.
 *
 * @param error - The error.
 * @returns The kind of page.
 */
function errorKindOf(error: unknown): ErrorKind {
  const kind: ErrorKind =
    isRouteErrorResponse(error) && error.status === 404
      ? "notFound"
      : isChunkLoadError(error)
        ? "unavailable"
        : "generic";

  return kind;
}

/**
 * Link back to the start page, which works offline (the clock).
 *
 * @returns The link.
 */
function BackHomeLink(): JSX.Element {
  const { t } = useI18n();
  const link = (
    <Link className={styles["button"]} to={routePaths.clock}>
      {t("error.backHome")}
    </Link>
  );

  return link;
}

/**
 * Content of the page for a path that does not exist (UX-023): a neutral text and the way to the
 * start page. Reloading would not help, so it is not offered.
 *
 * @returns The content.
 */
function NotFoundContent(): JSX.Element {
  const { t } = useI18n();
  const content = (
    <>
      <p>{t("error.notFound")}</p>
      <p>{t("notFound.hint")}</p>
      <div className={styles["row"]}>
        <BackHomeLink />
      </div>
    </>
  );

  return content;
}

/**
 * Content of the page for an unexpected error: what to do, reload and the way to the start page.
 *
 * @returns The content.
 */
function GenericErrorContent(): JSX.Element {
  const { t } = useI18n();
  const content = (
    <>
      <p>{t("screenError.text")}</p>
      <div className={styles["row"]}>
        <button
          type="button"
          className={cx(styles["button"], styles["primary"])}
          onClick={reloadPage}
        >
          {t("error.reload")}
        </button>
        <BackHomeLink />
      </div>
    </>
  );

  return content;
}

/** Properties of {@link UnavailableContent}. */
interface UnavailableContentProps {
  /** Whether the device is online. */
  readonly online: boolean;
}

/**
 * Content of the page for a screen whose code could not be loaded (UX-109). Offline it says that
 * the area is not available offline yet and that clocking works; a retry while still offline only
 * says so, because reloading without connection could leave the application. Online the retry
 * reloads the page.
 *
 * @param props - Component properties.
 * @returns The content.
 */
function UnavailableContent(props: UnavailableContentProps): JSX.Element {
  const { t } = useI18n();
  const { announce } = useAnnouncer();
  const [retriedOffline, setRetriedOffline] = useState(false);
  const content = (
    <>
      <p>{props.online ? t("screenError.unavailable.text") : t("screenError.offline.text")}</p>
      {retriedOffline && !props.online ? <p>{t("screenError.stillOffline")}</p> : null}
      <div className={styles["row"]}>
        <button
          type="button"
          className={cx(styles["button"], styles["primary"])}
          onClick={() => {
            if (props.online) {
              reloadPage();
            } else {
              setRetriedOffline(true);
              announce(t("screenError.stillOffline"));
            }
          }}
        >
          {t("error.retry")}
        </button>
        <BackHomeLink />
      </div>
    </>
  );

  return content;
}

/**
 * Returns title and content of the error page of a kind.
 *
 * @param kind - The kind of page.
 * @returns The view.
 */
function useErrorView(kind: ErrorKind): ErrorView {
  const { t } = useI18n();
  const online = useOnlineStatus();
  const views: Readonly<Record<ErrorKind, () => ErrorView>> = {
    notFound: () => ({ title: t("notFound.title"), content: <NotFoundContent /> }),
    unavailable: () => ({
      title: online ? t("screenError.unavailable.title") : t("screenError.offline.title"),
      content: <UnavailableContent online={online} />,
    }),
    generic: () => ({ title: t("screenError.title"), content: <GenericErrorContent /> }),
  };
  const view = views[kind]();

  return view;
}

/** Properties of {@link ScreenErrorView}. */
interface ScreenErrorViewProps {
  /** The kind of page. */
  readonly kind: ErrorKind;
}

/**
 * An error page inside the app shell: page heading that also names the document, then the
 * content, in the column of a screen.
 *
 * @param props - Component properties.
 * @returns The page.
 */
function ScreenErrorView(props: ScreenErrorViewProps): JSX.Element {
  const view = useErrorView(props.kind);
  const page = (
    <div className={styles["page"]}>
      <PageHeader title={view.title} />
      {view.content}
    </div>
  );

  return page;
}

/**
 * Page for a path that does not exist (UX-023), inside the app shell.
 *
 * @returns The page.
 */
export function NotFoundPage(): JSX.Element {
  const page = <ScreenErrorView kind="notFound" />;

  return page;
}

/** Properties of {@link ErrorFallback}. */
export interface ErrorFallbackProps {
  /** Shows the page for a path that does not exist instead of the generic error. */
  readonly notFound?: boolean | undefined;
}

/**
 * Page for an unexpected error while showing a screen, inside the app shell.
 *
 * @param props - Component properties.
 * @returns The page.
 */
export function ErrorFallback(props: ErrorFallbackProps = {}): JSX.Element {
  const page = <ScreenErrorView kind={props.notFound === true ? "notFound" : "generic"} />;

  return page;
}

/**
 * Reports the type of a route error without personal data: to the console and to the operator's
 * log (F-68). An unknown path is no failure; code that could not be loaded (offline, or removed by
 * a deployment) is a warning.
 *
 * @param error - The error.
 */
function useReportedError(error: unknown): void {
  const log = useClientLog();

  useEffect(() => {
    const name = error instanceof Error ? error.name : typeof error;
    console.error(`Screen failed: ${name}`);

    if (isRouteErrorResponse(error)) {
      if (error.status !== 404) {
        log.error("ScreenFailed", { errorCode: String(error.status) });
      }
    } else if (isChunkLoadError(error)) {
      log.warn("ScreenFailed", { error, errorCode: "ChunkLoadError" });
    } else {
      log.error("ScreenFailed", { error });
    }
  }, [error, log]);
}

/**
 * Error element of the screens inside the app shell (UX-109): the error replaces only the content
 * of `main`, so the app bar with the connectivity status and the navigation stay. A screen whose
 * code could not be loaded says so (offline: „Dieser Bereich ist offline noch nicht verfügbar“)
 * with a retry, an unknown path gets {@link NotFoundPage} and any other error the generic page.
 *
 * @returns The error page.
 */
export function ScreenErrorPage(): JSX.Element {
  const error = useRouteError();
  useReportedError(error);
  const page = <ScreenErrorView kind={errorKindOf(error)} />;

  return page;
}

/**
 * Error element outside the app shell (login callback, sign-in, an error of the shell itself): the
 * same pages as {@link ScreenErrorPage} in the shared layout of the public screens (UX-120), with
 * product mark, safe-area gutter, `main` and „Abmelden“ while a session exists.
 *
 * @returns The error page.
 */
export function RouteErrorPage(): JSX.Element {
  const error = useRouteError();
  useReportedError(error);
  const view = useErrorView(errorKindOf(error));
  const page = (
    <PublicLayout title={view.title} look="hero">
      {view.content}
    </PublicLayout>
  );

  return page;
}
